import React from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import ExternalLink from './Link/ExternalLink';

function isInternalUrl(url) {
    const exceptionList = [
        'recreation.gov',
        'recreationonestopprod.service-now.com'
    ];

    for (let i = 0; i < exceptionList.length; i += 1) {
        if (url.toLowerCase().indexOf(exceptionList[i]) !== -1) {
            return true;
        }
    }

    return false;
}

/**
 * Transform for better HTML
 * ===========================
 * This transform function is used for html that contains block elements such as
 * <p>. It's expected that this html is valid, or pretty much near valid html, so
 * minimal clean up is performed.
 */
function transformBetterHtml(node, index) {
    // wrap orphan text nodes in a paragraph
    if (node.type === 'text' && !node.parent) {
        return (
            <p key={`p-${index}`}>{convertNodeToElement(node, index, transformBetterHtml)}</p>
        );
    }

    // remove duplicate <br>s
    if (node.type === 'tag' && node.name === 'br' && node.prev && node.prev.type === 'tag' && node.prev.name === 'br') {
        return null;
    }

    // remove <br>s that don't have a parent
    if (node.type === 'tag' && node.name === 'br' && !node.parent) {
        return " ";
    }

    // Transforms for links
    if (node.type === 'tag' && node.name === 'a') {
        // Guard against bad HTML
        if (!node.attribs.href) {
            return null;
        }

        let url = node.attribs.href;

        // check if it's an internal URL
        if (isInternalUrl(url)) {
            url = url.replace(/(.*?).recreation.gov\/(.*?)\.pdf/, (m, $1, $2) => (
                `${process.env.CDN_ROOT}/${$2}.pdf`
            ));

            const newNode = node;
            newNode.attribs.rel = "noopener noreferrer";
            newNode.attribs.target = "_blank";
            newNode.attribs.href = url;
            return convertNodeToElement(newNode, index, transformBetterHtml);
        }
        // Otherwise if it has content we treat it as external
        else if (node.children && node.children[0]) {
            return (
                <ExternalLink
                    key={index}
                    url={url}>
                    {convertNodeToElement(node.children[0])}
                </ExternalLink>
            )
        }
        // if it's empty, we remove the link
        return null;
    }

    // Kill nodes that are just spaces
    // =======================
    if (node.type === 'text' && node.data === ' ') {
        return null;
    }

    return undefined;
}


/**
 * Transform for bad HTML
 * ===========================
 * This transform function is used for html that isn't complete and only contains
 * some inline elements.
 */
function transformBadHtml(node, index) {
    // wrap any orphan text nodes in a span
    if (node.type === 'text' && !node.parent) {
        return (
            <span key={`span-${index}`}>{convertNodeToElement(node, index, transformBadHtml)}</span>
        );
    }

    // remove all <br>s because they're too often used for non-symantec
    // presentation and lead to weird line lengths
    if (node.type === 'tag' && node.name === 'br') {
        return " ";
    }


    // Transforms for links
    if (node.type === 'tag' && node.name === 'a') {
        const internalPattern = /(.*?)recreation.gov\/(.*)/;
        let url = node.attribs.href;

        // if the url contains '.recreation.gov/' it's internal.
        if (internalPattern.test(url)) {
            url = url.replace(/(.*?).recreation.gov\/(.*?)\.pdf/, (m, $1, $2) => (
                `${process.env.CDN_ROOT}/${$2}.pdf`
            ));

            const newNode = node;
            newNode.attribs.rel = "noopener noreferrer";
            newNode.attribs.target = "_blank";
            newNode.attribs.href = url;
            return convertNodeToElement(newNode, index, transformBadHtml);
        }
        // Otherwise if it has content we treat it as external
        else if (node.children && node.children[0]) {
            return (
                <ExternalLink
                    key={index}
                    url={url}>
                    {convertNodeToElement(node.children[0])}
                </ExternalLink>
            )
        }
        // if it's empty, we remove the link
        return null;
    }

    // Kill nodes that are just spaces
    if (node.type === 'text' && node.data === ' ') {
        return null;
    }

    return undefined;
}


/**
 * Turns a string of html or html-like stuff into rendered html
 */
export default class DisplayHtmlContent extends React.PureComponent {
    static propTypes = {
        /** The html string to be rendered */
        html: PropTypes.string,

        /** Converts any img src with http to https  */
        shouldConvertImageSrcToHttps: PropTypes.bool,

        /** Converts all headings (regardless of their existing heading level) to the specified heading level */
        headingLevel: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),

        /** Gives all headings the specified appearance */
        headingAppearance: PropTypes.oneOf(["display", "h1", "h2", "h3", "h4", "h5", "h6"])
    };

    static defaultProps = {
        shouldConvertImageSrcToHttps: true,
        headingLevel: undefined,
        headingAppearance: undefined
    }

    render() {
        const {
            html,
            shouldConvertImageSrcToHttps,
            headingLevel,
            headingAppearance
        } = this.props;

        if (typeof html !== 'string') {
            return null;
        }

        let alteredHtml = html;

        if (shouldConvertImageSrcToHttps) {
            alteredHtml = alteredHtml.split('src="http://').join('src="https://');
        }

        if (headingLevel) {
            alteredHtml = alteredHtml.replace(/(<\/?h)(\d)\b/g, `$1${headingLevel}`);
        }

        if (headingAppearance) {
            alteredHtml = alteredHtml.replace(/<h\d\b/g, `$& class="${headingAppearance}"`);
        }

        const hasBlockElements = html.includes("<p>") || html.includes("<div>");

        if (hasBlockElements) {
            return (
                <div>
                    {ReactHtmlParser(alteredHtml, { transform: transformBetterHtml })}
                </div>
            );
        }

        return (
            <div className="rec-display-html-content">
                {ReactHtmlParser(alteredHtml, { transform: transformBadHtml })}
            </div>
        );
    }
}
